// EAM Music Ltd. — Brand Logo components (official logo supplied by the owner)
// One consistent identity everywhere:
//   • BrandMark / BrandLogoCompact → the round "EAM MUSIC · Record Label" emblem
//   • BrandLogo                    → emblem + "EAMMUSIC" wordmark (theme-aware text)
//   • BrandLogo useTextLogo        → wordmark only
//   • BrandLockup                  → full official lockup image ("4 years of EAMMUSIC publishing")
// Source assets live in /public/brand (generated from the official logo file).

const PUBLIC = process.env.PUBLIC_URL || "";
const EMBLEM_URL = `${PUBLIC}/brand/emblem-256.png`;
const EMBLEM_SMALL_URL = `${PUBLIC}/brand/emblem-128.png`;
const LOGO_URL = EMBLEM_URL;
const LOCKUP_URL = `${PUBLIC}/brand/logo-full.png`;            // blue wordmark — for light surfaces
const LOCKUP_DARK_URL = `${PUBLIC}/brand/logo-full-dark.png`;  // white wordmark — for dark surfaces
const LOGO_TEXT_URL = LOCKUP_URL;
const BRAND_NAME = "EAM MUSIC";

const MARK_SIZES = { xs: "w-6 h-6", sm: "w-8 h-8", md: "w-10 h-10", lg: "w-12 h-12", xl: "w-16 h-16" };
const TEXT_SIZES = { xs: "text-sm", sm: "text-base", md: "text-lg", lg: "text-xl", xl: "text-3xl" };
const WORDMARK_ONLY_SIZES = { xs: "text-lg", sm: "text-xl", md: "text-2xl", lg: "text-3xl", xl: "text-4xl md:text-5xl" };
const LOCKUP_HEIGHTS = { xs: "h-8", sm: "h-10", md: "h-12", lg: "h-16", xl: "h-20 md:h-24" };

/** Round record-label emblem. */
export function BrandMark({ size = "sm", className = "", title = "EAM Music" }) {
  const small = size === "xs" || size === "sm";
  return (
    <img
      src={small ? EMBLEM_SMALL_URL : EMBLEM_URL}
      alt={title}
      width={64}
      height={64}
      draggable={false}
      className={`${MARK_SIZES[size] || MARK_SIZES.sm} shrink-0 rounded-full object-contain select-none drop-shadow-[0_4px_12px_rgba(13,71,161,0.35)] ${className}`}
    />
  );
}

/** "EAMMUSIC" text wordmark (heavy, like the official logo). Uses currentColor → works in light & dark. */
export function BrandWordmark({ className = "", sizeClass = "text-lg" }) {
  return (
    <span
      className={`font-heading font-extrabold leading-none tracking-[0.02em] select-none whitespace-nowrap ${sizeClass} ${className}`}
      aria-label="EAM Music"
    >
      EAMMUSIC
    </span>
  );
}

/**
 * Full official lockup image. `variant`: "auto" (follows the app theme via the `dark` class),
 * "dark" (white lettering, for dark backgrounds) or "light" (blue lettering, for light backgrounds).
 */
export function BrandLockup({ size = "md", variant = "auto", className = "" }) {
  const h = LOCKUP_HEIGHTS[size] || LOCKUP_HEIGHTS.md;
  const img = (src, extra = "") => (
    <img src={src} alt="EAM Music Publishing" draggable={false} className={`${h} w-auto object-contain select-none ${extra} ${className}`} />
  );
  if (variant === "dark") return img(LOCKUP_DARK_URL);
  if (variant === "light") return img(LOCKUP_URL);
  return (
    <>
      {img(LOCKUP_URL, "dark:hidden")}
      {img(LOCKUP_DARK_URL, "hidden dark:block")}
    </>
  );
}

export function BrandLogo({ size = "md", showText = true, className = "", useTextLogo = false }) {
  if (useTextLogo) {
    return (
      <BrandWordmark
        sizeClass={WORDMARK_ONLY_SIZES[size] || WORDMARK_ONLY_SIZES.md}
        className={`text-foreground ${className}`}
      />
    );
  }

  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <BrandMark size={size} />
      {showText && <BrandWordmark sizeClass={TEXT_SIZES[size] || TEXT_SIZES.md} className="text-foreground" />}
    </div>
  );
}

export function BrandLogoCompact({ size = "sm", className = "" }) {
  return <BrandMark size={size} className={className} />;
}

export { LOGO_URL, LOGO_TEXT_URL, EMBLEM_URL, LOCKUP_URL, LOCKUP_DARK_URL, BRAND_NAME };
